import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { GET_CATEGORY_PAGE } from '@/graphql/queries';
import { NUMBER_OF_PRODUCTS_TO_SHOW } from 'commons/constants';
import getVisitorLocationQueryVariables from 'commons/getVisitorLocationQueryVariables';
import useUserLocation from 'global-state/userLocation/useUserLocation';
import { hasSidebarFacetFilters, mergeParentFiltersIntoCategoryPage, parseStyleCategoryPageSlug } from '../categoryPageSlugUtils';
import { DATA_SOURCE, PAGE_TYPE, getCategoryPageTypeConfig } from '../categoryPageTypes';
const hasUsableAlexandriaPage = (categoryPage, pageType) => !!categoryPage?.pageName || hasSidebarFacetFilters(categoryPage?.filters, pageType);

/**
 * Prefer SSR Alexandria data (CategoryPageWrapper / ProductAndCategoryWrapper).
 * Client fetches only when that payload is missing (e.g. legacy app). Parent
 * filter merge matches the SSR helper for style/category pages.
 *
 * A page type sourcing both its metadata and its facets from the descriptor
 * never queries Alexandria at all; its `seo` block stands in for the page.
 */
const useCategoryAlgoliaAlexandriaData = t0 => {
  const $ = _c(36);
  const {
    slug,
    serverCategoryData,
    pageType: t1
  } = t0;
  const pageType = t1 === undefined ? PAGE_TYPE.CATEGORY : t1;
  let t2;
  if ($[0] !== pageType) {
    t2 = getCategoryPageTypeConfig(pageType);
    $[0] = pageType;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    metadataSource,
    facetSource,
    allowsClientPageFetch,
    seo
  } = t2;
  const usesAlexandriaPage = metadataSource === DATA_SOURCE.PAGE || facetSource === DATA_SOURCE.PAGE;
  const userLocation = useUserLocation();
  const zipCode = userLocation?.zip ?? "";
  const t3 = userLocation?.stateShortName;
  let t4;
  if ($[2] !== t3) {
    t4 = getVisitorLocationQueryVariables(t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const locationVars = t4;
  const serverPage = serverCategoryData?.getCategoryPage;
  let t5;
  if ($[4] !== allowsClientPageFetch || $[5] !== pageType || $[6] !== serverPage || $[7] !== usesAlexandriaPage) {
    t5 = allowsClientPageFetch && usesAlexandriaPage && !hasUsableAlexandriaPage(serverPage, pageType);
    $[4] = allowsClientPageFetch;
    $[5] = pageType;
    $[6] = serverPage;
    $[7] = usesAlexandriaPage;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const needsClientFetch = t5;
  let t6;
  if ($[9] !== locationVars || $[10] !== slug || $[11] !== zipCode) {
    t6 = {
      slug,
      zipCode,
      ...locationVars,
      limit: NUMBER_OF_PRODUCTS_TO_SHOW
    };
    $[9] = locationVars;
    $[10] = slug;
    $[11] = zipCode;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = !needsClientFetch;
  let t8;
  if ($[13] !== t6 || $[14] !== t7) {
    t8 = {
      variables: t6,
      skip: t7
    };
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const {
    data: clientData
  } = useQuery(GET_CATEGORY_PAGE, t8);
  const page = (needsClientFetch ? clientData?.getCategoryPage : null) ?? serverPage;
  let t9;
  if ($[16] !== slug) {
    t9 = parseStyleCategoryPageSlug(slug);
    $[16] = slug;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const styleCategory = t9;
  let t10;
  if ($[18] !== allowsClientPageFetch || $[19] !== facetSource || $[20] !== page?.filters || $[21] !== pageType || $[22] !== styleCategory) {
    t10 = allowsClientPageFetch && facetSource === DATA_SOURCE.PAGE && !!styleCategory && !hasSidebarFacetFilters(page?.filters, pageType);
    $[18] = allowsClientPageFetch;
    $[19] = facetSource;
    $[20] = page?.filters;
    $[21] = pageType;
    $[22] = styleCategory;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const needsParentFilters = t10;
  const t11 = styleCategory?.categorySlug;
  let t12;
  if ($[24] !== locationVars || $[25] !== t11 || $[26] !== zipCode) {
    t12 = {
      slug: t11,
      zipCode,
      ...locationVars,
      limit: 1
    };
    $[24] = locationVars;
    $[25] = t11;
    $[26] = zipCode;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const t13 = !needsParentFilters;
  let t14;
  if ($[28] !== t12 || $[29] !== t13) {
    t14 = {
      variables: t12,
      skip: t13
    };
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const {
    data: parentData
  } = useQuery(GET_CATEGORY_PAGE, t14);
  let t15;
  if ($[31] !== page || $[32] !== pageType || $[33] !== parentData?.getCategoryPage?.filters || $[34] !== seo) {
    const mergedPage = mergeParentFiltersIntoCategoryPage(page, parentData?.getCategoryPage?.filters, pageType);
    const getCategoryPage = {
      ...(seo ?? {}),
      ...(mergedPage ?? {})
    };
    Object.entries(seo ?? {}).forEach(t16 => {
      const [key, value] = t16;
      if (getCategoryPage[key] == null) {
        getCategoryPage[key] = value;
      }
    });
    t15 = {
      getCategoryPage
    };
    $[31] = page;
    $[32] = pageType;
    $[33] = parentData?.getCategoryPage?.filters;
    $[34] = seo;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  return t15;
};
export default useCategoryAlgoliaAlexandriaData;